/**
 * LEAF-Q (Low Energy Availability in Females Questionnaire), adapted to be
 * applicable across sexes for RED-S screening (BW18 PR-H).
 *
 * Source: Melin et al. 2014, BJSM. The original 25-item form has scoring
 * across three sub-categories. Below is an 8-question subset that's
 * applicable to a male athlete on tirzepatide (the menstrual section is
 * dropped; injury/GI/illness questions are kept and weighted heavier).
 *
 * Risk level mapping:
 *   score < 4: low
 *   score 4-7: moderate
 *   score >= 8 OR symptoms_count >= 3: high
 */

export type LeafQQuestion = {
  id: string;
  prompt: string;
  options: Array<{ label: string; value: number }>;
  // If true, this question contributes to the symptoms_count when value > 0.
  is_symptom?: boolean;
};

export const LEAF_Q_QUESTIONS: LeafQQuestion[] = [
  {
    id: "fatigue",
    prompt: "How often have you felt unusually fatigued in the last month?",
    options: [
      { label: "Never", value: 0 },
      { label: "Sometimes", value: 1 },
      { label: "Often", value: 2 },
    ],
    is_symptom: true,
  },
  {
    id: "stress_fractures",
    prompt: "Any bone stress reactions or fractures in the last 12 months?",
    options: [
      { label: "No", value: 0 },
      { label: "1", value: 2 },
      { label: "2 or more", value: 3 },
    ],
    is_symptom: true,
  },
  {
    id: "gi_distress",
    prompt: "GI symptoms (bloating, diarrhea, constipation) frequency in the last month?",
    options: [
      { label: "Rarely", value: 0 },
      { label: "Weekly", value: 1 },
      { label: "Daily", value: 2 },
    ],
    is_symptom: true,
  },
  {
    id: "illness_frequency",
    prompt: "Number of upper-respiratory infections in the last 6 months?",
    options: [
      { label: "0-1", value: 0 },
      { label: "2-3", value: 1 },
      { label: "4 or more", value: 2 },
    ],
    is_symptom: true,
  },
  {
    id: "training_decline",
    prompt: "Have you noticed unexplained training performance decline?",
    options: [
      { label: "No", value: 0 },
      { label: "Maybe", value: 1 },
      { label: "Yes", value: 2 },
    ],
    is_symptom: true,
  },
  {
    id: "libido",
    prompt: "Any noticeable drop in libido or morning erections in the last 3 months?",
    options: [
      { label: "No", value: 0 },
      { label: "Some", value: 1 },
      { label: "Significant", value: 2 },
    ],
    is_symptom: true,
  },
  {
    id: "mood",
    prompt: "Mood changes (irritability, low mood) in the last month?",
    options: [
      { label: "No", value: 0 },
      { label: "Mild", value: 1 },
      { label: "Significant", value: 2 },
    ],
  },
  {
    id: "cold_intolerance",
    prompt: "Feeling cold more easily than peers (especially extremities)?",
    options: [
      { label: "No", value: 0 },
      { label: "Sometimes", value: 1 },
      { label: "Yes, often", value: 2 },
    ],
  },
];

export type LeafQAnswers = Record<string, number>;

export type LeafQResult = {
  score: number;
  symptoms_count: number;
  risk_level: "low" | "moderate" | "high";
};

export function scoreLeafQ(answers: LeafQAnswers): LeafQResult {
  let score = 0;
  let symptoms = 0;
  for (const q of LEAF_Q_QUESTIONS) {
    const value = answers[q.id] ?? 0;
    score += value;
    if (q.is_symptom && value > 0) symptoms++;
  }
  let risk_level: LeafQResult["risk_level"] = "low";
  if (score >= 8 || symptoms >= 3) risk_level = "high";
  else if (score >= 4) risk_level = "moderate";
  return { score, symptoms_count: symptoms, risk_level };
}
